import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';

interface GroupAdminUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AddForm {
  firstName: string;
  lastName: string;
  email: string;
}

const emptyForm: AddForm = { firstName: '', lastName: '', email: '' };

const GroupAdminTeamPage: React.FC = () => {
  const [users, setUsers] = useState<GroupAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<AddForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    api.get('/group-admin/users')
      .then((res) => setUsers(res.data.data ?? []))
      .finally(() => setLoading(false));
  }, []);

  const openModal = () => { setForm(emptyForm); setShowModal(true); };
  const closeModal = () => setShowModal(false);

  const handleCreate = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/group-admin/users', {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
      });
      setUsers((prev) => [res.data.data as GroupAdminUser, ...prev]);
      toast.success('Group admin created and credentials sent by email.');
      closeModal();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })?.response?.data?.errors?.[0]?.message;
      toast.error(msg ?? 'Failed to create group admin.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: GroupAdminUser) => {
    setTogglingId(user.id);
    try {
      const res = await api.patch(`/group-admin/users/${user.id}`, { isActive: !user.isActive });
      const updated = res.data.data as GroupAdminUser;
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, isActive: updated.isActive } : u));
      toast.success(`${user.firstName ?? user.email} ${updated.isActive ? 'reactivated' : 'deactivated'}.`);
    } catch {
      toast.error('Failed to update user status.');
    } finally {
      setTogglingId(null);
    }
  };

  const isFormValid = form.firstName.trim() && form.lastName.trim() && form.email.trim();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
              Group Admins
            </h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Admins with access to this group portal
            </p>
          </div>
          <button
            type="button"
            onClick={openModal}
            className="h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2 self-start md:self-auto"
          >
            <span className="material-symbols-outlined text-[20px]">person_add</span>
            Add Admin
          </button>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
              <span className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Loading team...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">manage_accounts</span>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-base">No group admins yet. Add one above.</p>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Admin</th>
                  <th className="text-left px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Status</th>
                  <th className="text-left px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Last Login</th>
                  <th className="text-left px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Added</th>
                  <th className="text-right px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => {
                  const isBusy = togglingId === u.id;
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 ${u.isActive ? 'bg-primary/10 dark:bg-primary/20 text-primary' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                            {(u.firstName ?? u.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="flex flex-col">
                            {u.firstName && (
                              <span className="text-sm font-bold text-slate-900 dark:text-white">
                                {u.firstName} {u.lastName}
                              </span>
                            )}
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-0.5">
                              {u.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-tight ${u.isActive ? 'text-primary' : 'text-slate-300 dark:text-slate-600'}`}>
                          <span className={`w-2 h-2 rounded-full ${u.isActive ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`} />
                          {u.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-sm font-medium text-slate-400 dark:text-slate-500">
                        {formatDate(u.lastLoginAt)}
                      </td>
                      <td className="px-6 py-5 text-sm font-medium text-slate-400 dark:text-slate-500">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-8 py-5 text-right">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => void handleToggleActive(u)}
                          className={`h-8 px-4 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 ml-auto ${
                            u.isActive
                              ? 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-500 border border-slate-200 dark:border-slate-700'
                              : 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/30'
                          }`}
                        >
                          {isBusy ? (
                            <div className="w-3 h-3 border-2 border-current border-t-transparent animate-spin rounded-full" />
                          ) : (
                            <span className="material-symbols-outlined text-[13px]">
                              {u.isActive ? 'person_off' : 'person_check'}
                            </span>
                          )}
                          {u.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="px-8 py-6 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-[28px]">person_add</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">Add Group Admin</h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Grant group portal access</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all shadow-sm"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>

            <div className="px-10 py-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    First Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Jane"
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                    className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Last Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Smith"
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                    className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Email Address <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  placeholder="jane@dealergroup.com"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all"
                />
              </div>

              <div className="p-5 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-2xl flex items-start gap-4">
                <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                  <span className="material-symbols-outlined text-[18px]">info</span>
                </div>
                <p className="text-xs font-bold text-blue-700 dark:text-blue-400 leading-relaxed">
                  Credentials will be dispatched automatically via email.
                  <span className="block mt-1 text-[10px] font-bold uppercase opacity-60">Security reset enforced on initial login.</span>
                </p>
              </div>
            </div>

            <div className="px-10 py-6 flex items-center justify-end gap-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/30">
              <button
                type="button"
                onClick={closeModal}
                className="px-8 h-11 rounded-xl font-bold border border-red-50 dark:border-red-900/10 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer text-xs uppercase tracking-widest"
              >
                Discard
              </button>
              <button
                type="button"
                disabled={saving || !isFormValid}
                onClick={() => void handleCreate()}
                className="h-11 px-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[18px]">send</span>
                    Create Admin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupAdminTeamPage;
